import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ChannelBadge, CHANNELS } from "@/components/channels";
import { Plus, Calendar, ShieldCheck, Trash2, Check, Share2, Pause, Play } from "lucide-react";

const statusColor = { "brouillon": "bg-secondary text-muted-foreground", "programmée": "bg-amber-500/15 text-amber-500", "active": "bg-emerald-500/15 text-emerald-500", "en pause": "bg-destructive/15 text-destructive" };

export default function Hub() {
  const [channels, setChannels] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", channels: [], asset_ids: [], budget: 100, scheduled_at: "" });
  const [compliance, setCompliance] = useState(null);

  const load = () => {
    api.get("/channels").then((r) => setChannels(r.data.channels)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data.campaigns)).catch(() => {});
    api.get("/library").then((r) => setAssets(r.data.assets.filter((a) => a.type === "image"))).catch(() => {});
  };
  useEffect(load, []);

  const toggleConn = async (ch) => {
    try {
      if (ch.connected) { await api.post("/channels/disconnect", { channel: ch.id }); toast.success(`${ch.name} déconnecté`); }
      else { await api.post("/channels/connect", { channel: ch.id }); toast.success(`${ch.name} connecté`); }
      load();
    } catch { toast.error("Erreur"); }
  };

  const toggleFormChannel = (id) => setForm((f) => ({ ...f, channels: f.channels.includes(id) ? f.channels.filter((c) => c !== id) : [...f.channels, id] }));
  const toggleFormAsset = (id) => setForm((f) => ({ ...f, asset_ids: f.asset_ids.includes(id) ? f.asset_ids.filter((a) => a !== id) : [...f.asset_ids, id] }));

  const createCampaign = async () => {
    if (!form.name.trim() || form.channels.length === 0) return toast.error("Nom et au moins un canal requis");
    try {
      await api.post("/campaigns", { ...form, scheduled_at: form.scheduled_at || null });
      toast.success("Campagne créée et poussée sur les réseaux !");
      setOpen(false); setForm({ name: "", channels: [], asset_ids: [], budget: 100, scheduled_at: "" }); load();
    } catch { toast.error("Erreur de création"); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === "en pause" ? "active" : "en pause";
    await api.patch(`/campaigns/${c.campaign_id}`, { status: next }); load();
  };
  const remove = async (id) => { await api.delete(`/campaigns/${id}`); toast.success("Campagne supprimée"); load(); };
  const runCompliance = async (id) => {
    const { data } = await api.post(`/campaigns/${id}/compliance`);
    setCompliance({ id, ...data });
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold flex items-center gap-2"><Share2 className="text-primary" /> Hub de diffusion</h1><p className="text-muted-foreground">Connectez vos réseaux et pilotez vos campagnes.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0" data-testid="new-campaign-btn"><Plus size={16} className="mr-1.5" /> Nouvelle campagne</Button></DialogTrigger>
          <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Créer une campagne</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nom de la campagne</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl" placeholder="Ex: Soldes été 2026" data-testid="campaign-name" /></div>
              <div><Label>Réseaux de diffusion</Label>
                <div className="grid grid-cols-5 gap-2 mt-1.5">
                  {Object.keys(CHANNELS).map((id) => (
                    <button key={id} onClick={() => toggleFormChannel(id)} className={`p-2 rounded-xl border flex flex-col items-center gap-1 ${form.channels.includes(id) ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`campaign-channel-${id}`}>
                      <ChannelBadge id={id} size={28} />
                    </button>
                  ))}
                </div>
              </div>
              {assets.length > 0 && (
                <div><Label>Visuels ({form.asset_ids.length} sélectionnés)</Label>
                  <div className="grid grid-cols-4 gap-2 mt-1.5 max-h-32 overflow-y-auto">
                    {assets.slice(0, 12).map((a) => (
                      <button key={a.asset_id} onClick={() => toggleFormAsset(a.asset_id)} className={`relative aspect-square rounded-lg overflow-hidden border-2 ${form.asset_ids.includes(a.asset_id) ? "border-primary" : "border-transparent"}`}>
                        <img src={a.data_url} alt="" className="w-full h-full object-cover" />
                        {form.asset_ids.includes(a.asset_id) && <div className="absolute inset-0 bg-primary/40 flex items-center justify-center"><Check size={18} className="text-white" /></div>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Budget (€/jour)</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })} className="mt-1.5 h-11 rounded-xl" data-testid="campaign-budget" /></div>
                <div><Label>Programmer (optionnel)</Label><Input type="date" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="campaign-schedule" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={createCampaign} className="rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0 w-full" data-testid="campaign-submit">Lancer la campagne</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Channels */}
      <div>
        <h3 className="font-display font-semibold text-lg mb-3">Connexions réseaux</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {channels.map((ch) => (
            <div key={ch.id} className="p-4 rounded-2xl border border-border bg-card" data-testid={`channel-card-${ch.id}`}>
              <div className="flex items-center justify-between mb-3"><ChannelBadge id={ch.id} /><span className={`h-2.5 w-2.5 rounded-full ${ch.connected ? "bg-emerald-500" : "bg-muted-foreground/40"}`} /></div>
              <p className="font-semibold text-sm">{ch.name}</p>
              <p className="text-xs text-muted-foreground mb-3">{ch.sub}</p>
              <Button size="sm" variant={ch.connected ? "outline" : "default"} onClick={() => toggleConn(ch)} className="w-full rounded-lg text-xs" data-testid={`channel-toggle-${ch.id}`}>
                {ch.connected ? "Déconnecter" : "Connecter"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Campaigns */}
      <div>
        <h3 className="font-display font-semibold text-lg mb-3">Mes campagnes</h3>
        {campaigns.length === 0 ? (
          <div className="p-10 rounded-2xl border border-dashed border-border bg-card text-center text-muted-foreground"><Calendar size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Aucune campagne. Créez-en une pour commencer.</p></div>
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <div key={c.campaign_id} className="p-5 rounded-2xl border border-border bg-card" data-testid={`campaign-${c.campaign_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-1">{c.channels.map((id) => <ChannelBadge key={id} id={id} size={30} />)}</div>
                    <div>
                      <p className="font-semibold">{c.name}</p>
                      <p className="text-xs text-muted-foreground">Budget {c.budget}€/j · {c.scheduled_at ? `Programmée ${c.scheduled_at}` : "Immédiat"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${statusColor[c.status] || "bg-secondary"}`}>{c.status}</span>
                    <button onClick={() => toggleStatus(c)} className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center" data-testid={`campaign-toggle-${c.campaign_id}`}>{c.status === "en pause" ? <Play size={15} /> : <Pause size={15} />}</button>
                    <button onClick={() => runCompliance(c.campaign_id)} className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center" title="Vérifier la conformité" data-testid={`campaign-compliance-${c.campaign_id}`}><ShieldCheck size={15} /></button>
                    <button onClick={() => remove(c.campaign_id)} className="h-8 w-8 rounded-lg hover:bg-destructive/10 text-destructive flex items-center justify-center" data-testid={`campaign-delete-${c.campaign_id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-border">
                  <div><p className="text-xs text-muted-foreground">ROAS</p><p className="font-display font-bold">{c.metrics.roas}x</p></div>
                  <div><p className="text-xs text-muted-foreground">Impressions</p><p className="font-display font-bold">{c.metrics.impressions.toLocaleString("fr-FR")}</p></div>
                  <div><p className="text-xs text-muted-foreground">Clics</p><p className="font-display font-bold">{c.metrics.clicks.toLocaleString("fr-FR")}</p></div>
                  <div><p className="text-xs text-muted-foreground">CA</p><p className="font-display font-bold">{c.metrics.revenue.toLocaleString("fr-FR")}€</p></div>
                </div>
                {compliance?.id === c.campaign_id && (
                  <div className="mt-4 pt-4 border-t border-border" data-testid={`compliance-result-${c.campaign_id}`}>
                    <p className="text-sm font-medium mb-2">Conformité : <span className={compliance.score >= 80 ? "text-emerald-500" : "text-amber-500"}>{compliance.score}%</span></p>
                    <div className="space-y-1">{compliance.checks.map((ch, i) => <p key={i} className="text-xs flex items-center gap-2 text-muted-foreground">{ch.status === "pass" ? <Check size={13} className="text-emerald-500" /> : <span className="text-amber-500">⚠</span>}{ch.rule}</p>)}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
